import { MemberInterface } from "@swivl-mono/types";
import { ChipSelect, SelectOutlet } from "@swivl-mono/ui";
import React, { useEffect, useMemo, useState } from "react";
import { FaUserGroup } from "react-icons/fa6";
import { IoIosArrowDown } from "react-icons/io";

import MembersModel from "../../../../Models/Member/Members.Model";
import { getMemberDisplayName } from "./AssigneeField";
import { TicketPropertyRow } from "./TicketPropertyRow";

interface SubscribersFieldProps {
  subscribedMemberIds: number[];
  onChange: (memberIds: number[]) => void;
  pending?: boolean;
  locked?: boolean;
}

export function SubscribersField({ subscribedMemberIds, onChange, pending, locked }: SubscribersFieldProps) {
  const [members, setMembers] = useState<MemberInterface[]>([]);
  const [selected, setSelected] = useState<string[]>(subscribedMemberIds.map(String));
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (locked) return;
    if (MembersModel.state.members?.length) {
      setMembers(MembersModel.state.members);
    } else {
      MembersModel.loadMembers().then(() => {
        setMembers(MembersModel.state.members ?? []);
      });
    }
  }, [locked]);

  useEffect(() => {
    // Don't clobber an in-progress edit; resync on exit.
    if (isEditing) return;
    setSelected(subscribedMemberIds.map(String));
  }, [subscribedMemberIds, isEditing]);

  const options = useMemo(() => members.map((m) => ({ label: getMemberDisplayName(m), value: String(m.id) })), [members]);

  const handleSelectionChange = (values: string[]): void => {
    setIsEditing(true);
    setSelected(values);
  };

  const handleDone = (): void => {
    setIsEditing(false);
    onChange(selected.map(Number));
  };

  const handleOpenChange = (open: boolean): void => {
    // Closing without Accept cancels the draft; let the resync catch up.
    if (!open) setIsEditing(false);
  };

  if (locked) {
    return (
      <TicketPropertyRow label="Subscribers" lockedAction="Updating subscribers">
        {subscribedMemberIds.length > 0 ? subscribedMemberIds.length : "0"}
      </TicketPropertyRow>
    );
  }

  return (
    <TicketPropertyRow label="Subscribers" pending={pending}>
      <ChipSelect
        styles={{ [SelectOutlet.POPOVER]: { minWidth: 240 } }}
        hasSearchInDropdown
        options={options}
        selectedValues={selected}
        onChange={handleSelectionChange}
        onAccept={handleDone}
        onOpenChange={handleOpenChange}
        customTrigger={({ isOpen }) => (
          <button className={`flex cursor-pointer items-center gap-1 rounded px-2 py-0.5 text-sm ${isOpen ? "bg-grey-100" : "hover:bg-grey-50"}`}>
            <FaUserGroup className="size-3 text-grey-600" />
            <span>{subscribedMemberIds.length > 0 ? subscribedMemberIds.length : "0"}</span>
            <IoIosArrowDown className={`h-3.5 w-3.5 text-grey-600 transition-transform ${isOpen ? "rotate-180" : ""}`} />
          </button>
        )}
      />
    </TicketPropertyRow>
  );
}
